/**
 * Authorization Reason Messages
 * 
 * Maps reason codes to user-friendly messages for UI display.
 * These messages are designed to:
 * - Be clear and actionable
 * - Avoid technical jargon
 * - Provide context where helpful
 * 
 * For i18n support, use the code as the key and provide translations.
 */

import type { ReasonCode, PolicyReason } from './types';

// ============================================================================
// Message Templates
// ============================================================================

/**
 * User-friendly messages for each reason code.
 * Some messages are templates with placeholders.
 */
export const REASON_MESSAGES: Record<ReasonCode | string, string> = {
  // Capability-level denials
  MISSING_CAPABILITY: "You don't have permission to perform this action.",
  NO_POLICY_MATCHED: "This action is not available in the current context.",
  
  // Allow reasons
  ADMIN_OVERRIDE: "Allowed via administrator access.",
  OWNER_EDIT_ALLOWED: "You can edit your own content.",
  ASSIGNEE_ACCESS: "Allowed as the assigned item owner.",
  STEWARD_ACCESS: "Allowed as the assigned item owner.",
  ROLE_ALLOWED: "Allowed based on your assigned role.",
  DEFAULT_ALLOW: "Action permitted.",
  
  // Deny reasons - ownership
  NOT_OWNER: "Only the owner can perform this action.",
  NOT_ASSIGNEE: "Only the assigned item owner can perform this action.",
  NOT_STEWARD: "Only the assigned item owner can perform this action.",
  NOT_PARTICIPANT: "You need to be a participant to perform this action.",
  
  // Deny reasons - state
  RESOURCE_LOCKED: "This item is locked and cannot be modified.",
  FIELD_LOCKED: "This field is locked and cannot be changed.",
  ALREADY_PUBLISHED: "Published items cannot be modified.",
  ALREADY_FINALIZED: "This has been finalized and cannot be changed.",
  
  // Deny reasons - timing
  SUBMISSION_WINDOW_CLOSED: "The submission window is closed.",
  WRONG_PHASE: "This action is not available in the current phase.",
  DEADLINE_PASSED: "The deadline for this action has passed.",
  NOT_YET_OPEN: "This is not yet available.",
  
  // Deny reasons - other
  CONTRIBUTIONS_FROZEN: "Contributions are currently frozen.",
  APPROVAL_REQUIRED: "This action requires approval.",
  MISSING_PREREQUISITE: "A required step must be completed first.",
};

// ============================================================================
// Message Formatting
// ============================================================================

/**
 * Get the message for a reason code.
 */
export function getReasonMessage(code: ReasonCode | string): string {
  return REASON_MESSAGES[code] || `Action not allowed: ${code}`;
}

/**
 * Format a reason with its data.
 * Replaces placeholders like {field}, {phase}, etc.
 */
export function formatReasonMessage(reason: PolicyReason): string {
  let message = getReasonMessage(reason.code);
  
  // Replace placeholders with data values
  if (reason.data) {
    for (const [key, value] of Object.entries(reason.data)) {
      const placeholder = `{${key}}`;
      message = message.replace(placeholder, String(value ?? ''));
    }
  }
  
  // Enhance specific messages with data
  message = enhanceMessageWithData(reason.code, message, reason.data);
  
  return message;
}

/**
 * Enhance messages with additional context from data.
 */
function enhanceMessageWithData(
  code: string,
  message: string,
  data?: Record<string, unknown>
): string {
  if (!data) return message;
  
  switch (code) {
    case 'FIELD_LOCKED':
      if (data.field) {
        return `The "${data.field}" field is locked and cannot be changed.`;
      }
      break;
      
    case 'WRONG_PHASE':
      if (data.currentPhase && data.requiredPhase) {
        return `This action is available in the ${data.requiredPhase} phase. You're currently in ${data.currentPhase}.`;
      }
      break;
      
    case 'RESOURCE_LOCKED':
      if (data.lockReason) {
        return `This item is locked: ${data.lockReason}`;
      }
      break;
      
    case 'ROLE_ALLOWED':
      if (data.role) {
        return `Allowed based on your ${data.role} role.`;
      }
      break;
  }
  
  return message;
}

// ============================================================================
// Remediation Hints
// ============================================================================

/**
 * Remediation hint for guiding users on what to do next.
 */
export interface RemediationHint {
  key: string;
  message: string;
  actionLabel?: string;
  actionType?: 'contact' | 'wait' | 'navigate' | 'request';
}

/**
 * Get remediation hints for a reason code.
 */
export function getRemediationHints(code: ReasonCode | string): RemediationHint[] {
  switch (code) {
    case 'MISSING_CAPABILITY':
      return [{
        key: 'request-permission',
        message: 'Contact an administrator to request this permission.',
        actionLabel: 'Request Access',
        actionType: 'request',
      }];
      
    case 'NOT_OWNER':
      return [{
        key: 'contact-owner',
        message: 'Contact the owner to request changes.',
        actionLabel: 'Message Owner',
        actionType: 'contact',
      }];
      
    case 'NOT_ASSIGNEE':
    case 'NOT_STEWARD':
      return [{
        key: 'contact-assignee',
        message: 'Contact the assigned item owner for assistance.',
        actionLabel: 'Message Item Owner',
        actionType: 'contact',
      }];
      
    case 'RESOURCE_LOCKED':
      return [{
        key: 'request-unlock',
        message: 'Request that the item be unlocked by the item owner or admin.',
        actionLabel: 'Request Unlock',
        actionType: 'request',
      }];
      
    case 'SUBMISSION_WINDOW_CLOSED':
      return [{
        key: 'wait-for-window',
        message: 'Wait for the next submission window to open.',
        actionType: 'wait',
      }];
      
    case 'WRONG_PHASE':
      return [{
        key: 'wait-for-phase',
        message: 'This action will be available when the phase changes.',
        actionType: 'wait',
      }];
      
    case 'CONTRIBUTIONS_FROZEN':
      return [{
        key: 'wait-unfreeze',
        message: 'Contributions will resume when unfrozen.',
        actionType: 'wait',
      }];
      
    case 'APPROVAL_REQUIRED':
      return [{
        key: 'request-approval',
        message: 'Submit a request for approval.',
        actionLabel: 'Request Approval',
        actionType: 'request',
      }];
      
    default:
      return [];
  }
}

// ============================================================================
// Summary Formatting
// ============================================================================

/**
 * Get a summary message for multiple reasons.
 */
export function formatReasonSummary(reasons: PolicyReason[]): string {
  if (reasons.length === 0) {
    return 'Action not allowed.';
  }
  
  if (reasons.length === 1) {
    return formatReasonMessage(reasons[0]);
  }
  
  // Multiple reasons - show primary and count
  const primary = formatReasonMessage(reasons[0]);
  return `${primary} (and ${reasons.length - 1} more)`;
}

/**
 * Get all reason messages as a list.
 */
export function formatReasonList(reasons: PolicyReason[]): string[] {
  return reasons.map(formatReasonMessage);
}

// ============================================================================
// Icon Mapping
// ============================================================================

/**
 * Map reason codes to icon names (for lucide-react or similar).
 */
export function getReasonIcon(code: ReasonCode | string): string {
  switch (code) {
    case 'RESOURCE_LOCKED':
    case 'FIELD_LOCKED':
      return 'Lock';
      
    case 'ALREADY_PUBLISHED':
    case 'ALREADY_FINALIZED':
      return 'CheckCircle';
      
    case 'SUBMISSION_WINDOW_CLOSED':
    case 'DEADLINE_PASSED':
    case 'NOT_YET_OPEN':
      return 'Clock';
      
    case 'WRONG_PHASE':
      return 'AlertCircle';
      
    case 'NOT_OWNER':
    case 'NOT_ASSIGNEE':
    case 'NOT_STEWARD':
    case 'NOT_PARTICIPANT':
      return 'UserX';
      
    case 'MISSING_CAPABILITY':
      return 'ShieldOff';
      
    case 'CONTRIBUTIONS_FROZEN':
      return 'Snowflake';
      
    case 'ADMIN_OVERRIDE':
      return 'Shield';
      
    case 'OWNER_EDIT_ALLOWED':
    case 'ASSIGNEE_ACCESS':
    case 'STEWARD_ACCESS':
      return 'Check';
      
    default:
      return 'AlertTriangle';
  }
}

// ============================================================================
// Color Mapping
// ============================================================================

/**
 * Map reason codes to semantic colors.
 */
export function getReasonColor(code: ReasonCode | string): 'error' | 'warning' | 'info' | 'success' {
  // Allow reasons
  if ([
    'ADMIN_OVERRIDE',
    'OWNER_EDIT_ALLOWED',
    'ASSIGNEE_ACCESS',
    'STEWARD_ACCESS',
    'ROLE_ALLOWED',
    'DEFAULT_ALLOW',
  ].includes(code)) {
    return 'success';
  }
  
  // Soft denials (timing, phase)
  if ([
    'SUBMISSION_WINDOW_CLOSED',
    'WRONG_PHASE',
    'NOT_YET_OPEN',
    'CONTRIBUTIONS_FROZEN',
  ].includes(code)) {
    return 'warning';
  }
  
  // Informational denials
  if ([
    'ALREADY_PUBLISHED',
    'ALREADY_FINALIZED',
  ].includes(code)) {
    return 'info';
  }
  
  // Hard denials
  return 'error';
}
